import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"
import localeUK from "air-datepicker/locale/en"

export default class extends Controller {
  static values = {
    options: Object,
    range: Boolean
  }

  connect() {
    this.isSelectingFromCalendar = false
    this.userPreferredFormat = 'ordinal' // Default to ordinal format

    const baseOptions = {
      autoClose: true,
      locale: localeUK,
      startDay: 1,
      firstDay: 1,
      position: this.positionDatepicker.bind(this),
      navTitles: {
        days: '<span class="text-paragraph-m text-black-1000 font-medium">MMMM yyyy</span>',
        months: '<span class="text-paragraph-m text-black-1000 font-medium">yyyy</span>',
      },

      dateFormat: (date /* Date */) => {
        return this.formatDateOrdinal(date)
      },

      onSelect: () => {
        this.isSelectingFromCalendar = true
        const elem = this.element.tagName === "INPUT"
          ? this.element
          : this.element.querySelector("input")

        if (elem) {
          this.clearError(elem)
          this.validateDate({ target: elem })
        }
      },

      onRenderCell: ({ date, cellType }, dp) => {
        if (cellType !== 'day') return
        const classes = ["air-picker-cell"]

        const sel = dp?.selectedDates || []

        // helpers
        const isSameDay = (a, b) =>
          a && b &&
          a.getFullYear() === b.getFullYear() &&
          a.getMonth() === b.getMonth() &&
          a.getDate() === b.getDate()


        const isSelected = sel.some(d => isSameDay(d, date))
        if (isSelected) {
          classes.push("air-picker-cell--selected")
        } else if (this.rangeValue && sel.length === 2) {
          const [from, to] = sel[0] < sel[1] ? sel : [sel[1], sel[0]]
          const t = date.setHours(0,0,0,0)
          const f = from.setHours(0,0,0,0)
          const tt = to.setHours(0,0,0,0)
          if (t > f && t < tt) {
            classes.push("air-picker-cell--in-range")
          }
        }

        return { classes: classes.join(' ') }
      },

      buttons: [
        {
          content: 'Cancel',
          className: 'air-picker-secondary-button',
          onClick: (dp) => dp.hide()
        },
        {
          content: 'Apply',
          className: 'air-picker-primary-button',
          onClick: (dp) => dp.hide()
        }
      ]
    }

    const merged = { ...baseOptions, ...(this.optionsValue || {}) }

    const elem = this.element.tagName === "INPUT"
      ? this.element
      : this.element.querySelector("input")

    if (!elem) return

    if (this.rangeValue && merged.range !== true) merged.range = true

    this.picker = new AirDatepicker(elem, merged)
    this.inputElement = elem

    this.boundHideOnScroll = this.hideOnScroll.bind(this)
    this.scrollableParents = this.getScrollableParents(this.element)
    this.scrollableParents.forEach(parent => {
      parent.addEventListener('scroll', this.boundHideOnScroll, { passive: true })
    })

    // Set initial date after creating the picker
    if (elem.value) {
      // Detect format from initial value
      const detectedFormat = this.detectFormat(elem.value)
      if (detectedFormat) {
        this.userPreferredFormat = detectedFormat
      }

      const initialDate = this.parseFormattedDate(elem.value)
      if (initialDate) {
        this.picker.selectDate(initialDate)
      }
    }

    this.addValidationListeners(elem)
  }

  addValidationListeners(input) {
    this.boundHandleInput = this.handleInput.bind(this)
    this.boundHandleManualInput = this.handleManualInput.bind(this)
    this.boundValidateDate = this.validateDate.bind(this)
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)

    input.addEventListener('input', this.boundHandleInput)
    input.addEventListener("blur", this.boundHandleManualInput)
    input.addEventListener("change", this.boundValidateDate)

    // Use a small delay to ensure form is in the DOM when modal is loaded dynamically
    this.formListenerTimeout = setTimeout(() => {
      const form = input.closest('form')
      if (form) {
        form.addEventListener('submit', this.boundHandleFormSubmit)
      }
    }, 0)
  }

  handleInput(event) {
    // Don't format if this is a calendar selection
    if (this.isSelectingFromCalendar) {
      return
    }

    const input = event.target
    const oldValue = input.value
    const oldCursorPosition = input.selectionStart

    // Detect if user is typing numbers (slash format)
    // If they're typing only digits and slashes, use slash format
    const hasOnlyDigitsAndSlashes = /^[\d\/]*$/.test(oldValue)

    if (hasOnlyDigitsAndSlashes && oldValue.length > 0) {
      this.userPreferredFormat = 'slash'

      // Remove all non-digits
      const digitsOnly = oldValue.replace(/\D/g, '').slice(0, 8) // Max 8 digits for DD/MM/YYYY

      // Build formatted string
      let formatted = ''

      // Add day (DD)
      if (digitsOnly.length > 0) {
        formatted = digitsOnly.slice(0, 2)

        // Add first slash after day
        if (digitsOnly.length >= 2) {
          formatted += '/'
        }
      }

      // Add month (MM)
      if (digitsOnly.length > 2) {
        formatted += digitsOnly.slice(2, 4)

        // Add second slash after month
        if (digitsOnly.length >= 4) {
          formatted += '/'
        }
      }

      // Add year (YYYY)
      if (digitsOnly.length > 4) {
        formatted += digitsOnly.slice(4, 8)
      }

      // Calculate new cursor position
      if (formatted !== oldValue) {
        input.value = formatted

        // Count digits that were before the cursor in the old value
        const digitsBeforeCursor = oldValue.slice(0, oldCursorPosition).replace(/\D/g, '').length

        // Find position in new formatted string after the same number of digits
        // This will automatically place cursor after any slashes that were added
        let digitCount = 0
        let newCursorPosition = formatted.length // Default to end

        for (let i = 0; i < formatted.length; i++) {
          if (/\d/.test(formatted[i])) {
            digitCount++
            if (digitCount === digitsBeforeCursor) {
              // Set cursor right after this digit
              newCursorPosition = i + 1
              // If the next character is a slash, move cursor after it
              if (formatted[i + 1] === '/') {
                newCursorPosition = i + 2
              }
              break
            }
          }
        }

        input.setSelectionRange(newCursorPosition, newCursorPosition)
      }

      // If we have a complete date (8 digits), try to update the calendar
      if (digitsOnly.length === 8) {
        const parsedDate = this.parseFormattedDate(formatted)
        if (parsedDate) {
          this.picker.selectDate(parsedDate, { silent: true })
          this.clearError(input)
        }
      } else if (digitsOnly.length > 0) {
        // Remove error while typing if it exists
        this.clearError(input)
      }
    } else if (oldValue.length === 0) {
      // Reset to default format when input is cleared
      this.userPreferredFormat = 'ordinal'
    }
  }

  handleManualInput(event) {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }

    this.blurTimeout = setTimeout(() => {
      // If the datepicker is still visible, don't process blur
      // This allows navigation through months without interruption
      if (this.picker && this.picker.visible) {
        return
      }

      const input = event.target
      const value = input.value.trim()

      // If this was a calendar selection, just reset the flag and return
      if (this.isSelectingFromCalendar) {
        this.isSelectingFromCalendar = false
        return
      }

      // If empty, clear the picker
      if (!value) {
        this.picker.clear()
        this.clearError(input)
        this.userPreferredFormat = 'ordinal' // Reset to default
        return
      }

      // Detect which format the user entered
      const detectedFormat = this.detectFormat(value)
      if (detectedFormat) {
        this.userPreferredFormat = detectedFormat
      }

      // Try to parse the manual input
      const parsedDate = this.parseFormattedDate(value)

      if (parsedDate) {
        // Valid date - update the picker
        this.picker.selectDate(parsedDate, { silent: true })
        // Reformat the input value using the detected format
        input.value = this.formatDate(parsedDate)
        this.clearError(input)
      } else {
        // Invalid format - show error
        this.showError(input, "Please enter a valid date in DD/MM/YYYY or ordinal format (e.g., 18th Sep 2025)")
      }
    }, 100)
  }

  formatDate(date) {
    if (this.userPreferredFormat === 'ordinal') {
      return this.formatDateOrdinal(date)
    } else {
      return this.formatDateSlash(date)
    }
  }

  formatDateSlash(date) {
    const d = String(date.getDate()).padStart(2, '0')
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const y = date.getFullYear()
    return `${d}/${m}/${y}`
  }

  formatDateOrdinal(date) {
    const day = date.getDate()
    const month = this.getMonthAbbreviation(date.getMonth())
    const year = date.getFullYear()
    const ordinalSuffix = this.getOrdinalSuffix(day)
    return `${day}${ordinalSuffix} ${month} ${year}`
  }

  getOrdinalSuffix(day) {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1: return 'st'
      case 2: return 'nd'
      case 3: return 'rd'
      default: return 'th'
    }
  }

  getMonthAbbreviation(monthIndex) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return months[monthIndex]
  }

  getMonthFromAbbreviation(abbr) {
    const months = {
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
      'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    }
    return months[abbr.toLowerCase()]
  }

  detectFormat(dateString) {
    // Check for slash format (DD/MM/YYYY)
    if (/^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4}$/.test(dateString)) {
      return 'slash'
    }

    // Check for ordinal format (18th Sep 2025)
    if (/^\d{1,2}(st|nd|rd|th)?\s+[a-zA-Z]{3}\s+\d{4}$/.test(dateString)) {
      return 'ordinal'
    }

    return null
  }

  handleFormSubmit(event) {
    const elem = this.element.tagName === "INPUT"
      ? this.element
      : this.element.querySelector("input")

    if (!elem || elem.disabled) return

    const isValid = this.validateDate({ target: elem })
    if (!isValid) {
      event.preventDefault()
      event.stopPropagation()
      elem.focus()
    }
  }

  validateDate(event) {
    const input = event.target
    const value = input.value.trim()

    this.clearError(input)

    const isRequired = input.dataset.required === 'true'
    const fieldLabel = this.getFieldLabel()

    if (!value) {
      if (isRequired) {
        this.showError(input, `${fieldLabel} can't be blank`)
        return false
      }
      return true
    }

    // Try to parse the date (works for both formats)
    const parsedDate = this.parseFormattedDate(value)
    if (!parsedDate) {
      this.showError(input, "Please enter a valid date in DD/MM/YYYY or ordinal format (e.g., 18th Sep 2025)")
      return false
    }

    return true
  }

  getFieldLabel() {
    const label = this.element.querySelector('label')
    if (label) {
      const labelText = label.textContent.trim()
      return labelText.replace(/\s*\*\s*$/, '')
    }
    return 'Date'
  }

  showError(input, message) {
    this.toggleErrorState(input, true)

    const helperId = `${input.id}__helper`
    let helperText = this.element.querySelector(`#${helperId}`)

    if (!helperText) {
      helperText = document.createElement('p')
      helperText.id = helperId
      helperText.className = 'mt-8 text-paragraph-s text-red-1000'
      this.element.appendChild(helperText)
    } else {
      helperText.style.display = ''
      helperText.className = 'mt-8 text-paragraph-s text-red-1000'
    }

    helperText.textContent = message
  }

  clearError(input) {
    const helperId = `${input.id}__helper`
    const helperText = this.element.querySelector(`#${helperId}`)

    if (helperText?.dataset.backendError === 'true') return

    this.toggleErrorState(input, false)

    if (helperText) {
      helperText.style.display = 'none'
    }
  }

  toggleErrorState(input, hasError) {
    const wrapper = this.element.querySelector('.group')
    const icon = this.element.querySelector('.w-40')
    const iconSpan = icon?.querySelector('span')

    if (hasError) {
      wrapper?.classList.add('has-error')
      input.classList.add('border-red-300')
      icon?.classList.remove('border-black-100')
      icon?.classList.add('border-red-300')
      iconSpan?.classList.remove('text-black-400')
      iconSpan?.classList.add('text-red-1000')
    } else {
      wrapper?.classList.remove('has-error')
      input.classList.remove('border-red-300')
      icon?.classList.remove('border-red-300')
      icon?.classList.add('border-black-100')
      iconSpan?.classList.remove('text-red-1000')
      iconSpan?.classList.add('text-black-400')
    }
  }

  parseFormattedDate(dateString) {
    // Try to parse as DD/MM/YYYY format first
    const slashRegex = /^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/
    const slashMatch = dateString.match(slashRegex)

    if (slashMatch) {
      const [, day, month, year] = slashMatch
      const dayNum = parseInt(day, 10)
      const monthNum = parseInt(month, 10)
      const yearNum = parseInt(year, 10)

      // Validate ranges
      if (dayNum < 1 || dayNum > 31) return null
      if (monthNum < 1 || monthNum > 12) return null

      // Create date (month is 0-indexed in JavaScript Date)
      const date = new Date(yearNum, monthNum - 1, dayNum)

      // Validate it's actually valid (e.g., not 31/02)
      if (date.getDate() !== dayNum || date.getMonth() !== monthNum - 1 || date.getFullYear() !== yearNum) {
        return null
      }

      return date
    }

    // Try to parse as ordinal format (e.g., "18th Sep 2025")
    const ordinalRegex = /^(\d{1,2})(st|nd|rd|th)?\s+([a-zA-Z]{3})\s+(\d{4})$/
    const ordinalMatch = dateString.match(ordinalRegex)

    if (ordinalMatch) {
      const [, day, , monthAbbr, year] = ordinalMatch
      const dayNum = parseInt(day, 10)
      const yearNum = parseInt(year, 10)
      const monthNum = this.getMonthFromAbbreviation(monthAbbr)

      // Validate ranges
      if (dayNum < 1 || dayNum > 31) return null
      if (monthNum === undefined || monthNum < 0 || monthNum > 11) return null

      // Create date (month is 0-indexed in JavaScript Date)
      const date = new Date(yearNum, monthNum, dayNum)

      // Validate it's actually valid (e.g., not 31/02)
      if (date.getDate() !== dayNum || date.getMonth() !== monthNum || date.getFullYear() !== yearNum) {
        return null
      }

      return date
    }

    return null
  }

  positionDatepicker({ $datepicker, $target }) {
    const offset = 12
    const targetRect = $target.getBoundingClientRect()
    const parentRect = $datepicker.offsetParent.getBoundingClientRect()
    const pickerHeight = $datepicker.offsetHeight
    const requiredSpace = pickerHeight + offset
    const spaceBelow = window.innerHeight - targetRect.bottom
    const showAbove = spaceBelow < requiredSpace && targetRect.top > requiredSpace

    // Direction classes control which edge the pointer arrow renders on
    $datepicker.classList.toggle('-top-left-', showAbove)
    $datepicker.classList.toggle('-bottom-left-', !showAbove)

    const top = showAbove
      ? targetRect.top - parentRect.top - pickerHeight - offset
      : targetRect.bottom - parentRect.top + offset

    $datepicker.style.left = `${targetRect.left - parentRect.left}px`
    $datepicker.style.top = `${top}px`
  }

  hideOnScroll() {
    if (this.picker?.visible) {
      this.picker.hide()
    }
  }

  getScrollableParents(element) {
    const parents = []
    let parent = element.parentElement
    while (parent) {
      const { overflow, overflowY } = window.getComputedStyle(parent)
      if (/(auto|scroll)/.test(overflow) || /(auto|scroll)/.test(overflowY)) {
        parents.push(parent)
      }
      parent = parent.parentElement
    }
    return parents
  }

  disconnect() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout)
    }

    if (this.formListenerTimeout) {
      clearTimeout(this.formListenerTimeout)
    }

    if (this.scrollableParents && this.boundHideOnScroll) {
      this.scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', this.boundHideOnScroll)
      })
    }

    if (this.inputElement) {
      if (this.boundHandleInput) {
        this.inputElement.removeEventListener('input', this.boundHandleInput)
      }
      if (this.boundHandleManualInput) {
        this.inputElement.removeEventListener('blur', this.boundHandleManualInput)
      }
      if (this.boundValidateDate) {
        this.inputElement.removeEventListener('change', this.boundValidateDate)
      }

      // Remove form submit listener
      const form = this.inputElement.closest('form')
      if (form && this.boundHandleFormSubmit) {
        form.removeEventListener('submit', this.boundHandleFormSubmit)
      }
    }
    this.picker?.destroy()
  }
}
